/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { Breadcrumb, Button, Card, Form, Input, InputNumber, Modal, Spin, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import {
  useGetPendingUsersQuery,
  useUpdatePendingUserStatusMutation,
  useGetUsersQuery,
  useUpdateUserMaxFarmsMutation,
  type PendingUser,
  type AdminUser,
} from '../../api/adminApi';
import { toastError, toastSuccess } from '../../utils/toast';
import { useSelector } from 'react-redux';

interface StatusModalState {
  id: string | number;
  currentStatus?: string | null;
}

interface MaxFarmsModalState {
  id: string | number;
  currentValue?: number | null;
}

const statusColorMap: Record<string, string> = {
  approved: 'green',
  active: 'green',
  rejected: 'red',
  declined: 'red',
  pending: 'gold',
  invited: 'blue',
  inactive: 'default',
};

const formatDate = (value?: string | null) => {
  if (!value) return '';
  const idx = value.indexOf('T');
  return idx > 0 ? value.slice(0, idx) : value;
};

const renderStatusTag = (status?: string | null) => {
  if (!status) return null;
  const normalized = status.toLowerCase();
  const color = statusColorMap[normalized] || 'default';
  return <Tag color={color}>{status}</Tag>;
};

const AdminUsersPage: React.FC = () => {
  const user = useSelector((state: any) => state.auth.user);
  const userActions = useSelector((state: any) => state.auth.user?.actions);
  
  const canManageUsers = useMemo(() => {
    if (user && typeof userActions === 'object' && 'global' in userActions) {
      return (
        user.actions?.global?.farms?.includes('users.update') ||
        user.actions?.global?.farms?.includes('users.manage') ||
        ['admin', 'superadmin', 'superuser'].includes(String(user.role).toLowerCase())
      );
    }

    return Array.isArray(userActions) && userActions.includes('users.update');
  }, [user, userActions]);

  const { data: pendingUsers, isLoading: pendingLoading, isError: pendingError } = useGetPendingUsersQuery(undefined, {
    skip: !canManageUsers,
  });
  const { data: allUsers, isLoading: usersLoading, isError: usersError } = useGetUsersQuery(undefined, {
    skip: !canManageUsers,
  });
  const [updateStatus, { isLoading: updatingStatus }] = useUpdatePendingUserStatusMutation();
  const [updateMaxFarms, { isLoading: updatingMaxFarms }] = useUpdateUserMaxFarmsMutation();

  const [statusModal, setStatusModal] = useState<StatusModalState | null>(null);
  const [form] = Form.useForm<{ status: string }>();
  const [maxFarmsModal, setMaxFarmsModal] = useState<MaxFarmsModalState | null>(null);
  const [maxFarmsForm] = Form.useForm<{ max_farms: number }>();

  const openStatusModal = (user: PendingUser) => {
    setStatusModal({ id: user.id, currentStatus: user.status });
    form.setFieldsValue({ status: user.status || '' });
  };

  const handleStatusSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!statusModal) return;
      await updateStatus({ id: statusModal.id, status: values.status }).unwrap();
      toastSuccess('Статус обновлён');
      setStatusModal(null);
      form.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const openMaxFarmsModal = (user: AdminUser) => {
    setMaxFarmsModal({ id: user.id, currentValue: user.max_farms ?? undefined });
    maxFarmsForm.setFieldsValue({ max_farms: user.max_farms ?? 0 });
  };

  const handleMaxFarmsSubmit = async () => {
    try {
      const values = await maxFarmsForm.validateFields();
      if (!maxFarmsModal) return;
      await updateMaxFarms({ id: maxFarmsModal.id, max_farms: values.max_farms }).unwrap();
      toastSuccess('Максимальное количество ферм обновлено');
      setMaxFarmsModal(null);
      maxFarmsForm.resetFields();
    } catch (error: any) {
      if (!error?.errorFields) {
        toastError(error);
      }
    }
  };

  const pendingColumns: ColumnsType<PendingUser> = [
    {
      title: 'Имя',
      dataIndex: 'username',
      render: (value, record) =>
        value || record.name || record.email || record.phone || record.id,
    },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Телефон', dataIndex: 'phone' },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (value) => renderStatusTag(value),
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      render: formatDate,
    },
    {
      title: 'Действия',
      render: (_, record) => (
        <Button type="link" onClick={() => openStatusModal(record)}>
          Изменить статус
        </Button>
      ),
    },
  ];

  const usersColumns: ColumnsType<AdminUser> = [
    {
      title: 'Имя',
      dataIndex: 'username',
      render: (value, record) => value || record.name || record.email || record.phone || record.id,
    },
    { title: 'Email', dataIndex: 'email' },
    { title: 'Телефон', dataIndex: 'phone' },
    {
      title: 'Статус',
      dataIndex: 'status',
      render: (value) => renderStatusTag(value),
    },
    { title: 'Роль', dataIndex: 'role' },
    {
      title: 'Макс. ферм',
      dataIndex: 'max_farms',
      render: (value: number | null | undefined) =>
        value !== undefined && value !== null ? value : '—',
    },
    {
      title: 'Создан',
      dataIndex: 'created_at',
      render: formatDate,
    },
    {
      title: 'Обновлён',
      dataIndex: 'updated_at',
      render: formatDate,
    },
    {
      title: 'Действия',
      render: (_, record) => (
        <Button type="link" onClick={() => openMaxFarmsModal(record)}>
          Изменить лимит ферм
        </Button>
      ),
    },
  ];

  if (!canManageUsers) {
    return (
      <MainLayout
        breadcrumb={
          <Breadcrumb
            items={[
              { title: 'Администрирование' },
              { title: 'Пользователи' },
            ]}
          />
        }
      >
        <Card>
          <Card.Meta title="Недостаточно прав" description="Доступ к управлению пользователями ограничен." />
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout
      breadcrumb={
        <Breadcrumb
          items={[
            { title: 'Администрирование' },
            { title: 'Пользователи' },
          ]}
        />
      }
    >
      <Tabs
        defaultActiveKey="pending"
        items={[
          {
            key: 'pending',
            label: 'На подтверждении',
            children: (
              <Spin spinning={pendingLoading}>
                {pendingError ? (
                  <Card>
                    <Card.Meta title="Ошибка" description="Не удалось загрузить список ожидающих пользователей." />
                  </Card>
                ) : (
                  <Table
                    rowKey={(user) => String(user.id)}
                    dataSource={pendingUsers || []}
                    columns={pendingColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Spin>
            ),
          },
          {
            key: 'all',
            label: 'Все пользователи',
            children: (
              <Spin spinning={usersLoading}>
                {usersError ? (
                  <Card>
                    <Card.Meta title="Ошибка" description="Не удалось загрузить список пользователей." />
                  </Card>
                ) : (
                  <Table
                    rowKey={(user) => String(user.id)}
                    dataSource={allUsers || []}
                    columns={usersColumns}
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Spin>
            ),
          },
        ]}
      />

      <Modal
        title="Изменение статуса"
        open={!!statusModal}
        onCancel={() => {
          setStatusModal(null);
          form.resetFields();
        }}
        onOk={handleStatusSubmit}
        confirmLoading={updatingStatus}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Статус"
            name="status"
            rules={[{ required: true, message: 'Введите статус' }]}
            initialValue={statusModal?.currentStatus || ''}
          >
            <Input placeholder="например, approved" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Изменение лимита ферм"
        open={!!maxFarmsModal}
        onCancel={() => {
          setMaxFarmsModal(null);
          maxFarmsForm.resetFields();
        }}
        onOk={handleMaxFarmsSubmit}
        confirmLoading={updatingMaxFarms}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={maxFarmsForm} layout="vertical">
          <Form.Item
            label="Максимальное количество ферм"
            name="max_farms"
            rules={[{ required: true, message: 'Введите число' }]}
            initialValue={maxFarmsModal?.currentValue ?? 0}
          >
            <InputNumber min={0} css={commonStyles.fullWidth} />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AdminUsersPage;
